/**
 * Leave reports and holiday bulk import — schemas (Phase 4d).
 *
 * READ-ONLY REPORTS over the leave ledger and requests, for HR. Every day count is a decimal STRING
 * (a balance may be fractional, and a historical over-debit can leave it negative), never a float.
 * Nothing here is money. A report's period is a pair of calendar dates: a ledger row belongs to the
 * period when its `valid_from` DATE falls inside it, and a leave request when its own start/end
 * DATE columns do. The reports compare dates with dates and never convert an instant to a day, so
 * no server-zone arithmetic is involved (CLAUDE.md invariant 3). Which day a ledger writer stamps
 * is the writer's rule (today it is the UTC date of the write).
 *
 * WHICH PEOPLE. A report covers the people the caller's `leave.report:read` grants reach (a
 * department-scoped HR Executive sees that department). Out-of-scope people are absent, never 403.
 *
 * HOLIDAY IMPORT takes either the text of a CSV or already-parsed rows. Every row is validated on
 * its own and every problem is reported; a file with any error row applies nothing. A holiday that
 * already exists for a location and date is a SKIP, not an error, so re-uploading a file is safe.
 *
 * RN-SAFE: zod only, no Node built-ins.
 */

import { z } from 'zod';
import { dateOnlySchema, paginatedSchema, paginationQuerySchema, uuidSchema } from './schemas/common.js';

/** Same shape as `signedDaysSchema` in `leave-balance-admin.contract.ts`, redeclared so this file
 * depends on no contract (contracts import it, never the reverse). */
const dayFigureSchema = z.string().regex(/^-?\d+(\.\d+)?$/, 'Must be a decimal day count');

// ---------------------------------------------------------------------------
// Limits (shared so the screen can say them and the API can enforce them)
// ---------------------------------------------------------------------------

/** Longest period the register, lapse and loss-of-pay reports accept. */
export const LEAVE_REPORT_MAX_PERIOD_DAYS = 366;
/** Longest period the trends report accepts (two years of months). */
export const LEAVE_TRENDS_MAX_PERIOD_DAYS = 731;
/** Most rows one CSV export returns; past it the file is cut and `truncated` is true. */
export const LEAVE_REPORT_EXPORT_MAX_ROWS = 20_000;
/** Most leave requests the trends report will aggregate in one call. */
export const LEAVE_TRENDS_MAX_REQUESTS = 25_000;
/** Most lapse entries one lapse report call will gather before asking for narrower filters. */
export const LEAVE_LAPSE_MAX_ENTRIES = 20_000;
/** Most (month, department, leave type) cells the trends report returns. */
export const LEAVE_TRENDS_MAX_CELLS = 5000;

// ---------------------------------------------------------------------------
// Shared filters
// ---------------------------------------------------------------------------

const reportFilterShape = {
  /** First day of the period, inclusive. */
  from: dateOnlySchema,
  /** Last day of the period, inclusive. Must not be before `from`. */
  to: dateOnlySchema,
  legalEntityId: uuidSchema.optional(),
  departmentId: uuidSchema.optional(),
  locationId: uuidSchema.optional(),
  leaveTypeId: uuidSchema.optional(),
  /** Matches first name, last name or employee code, case-insensitively. */
  search: z.string().trim().max(100).optional(),
};

/** The people columns every per-person report row carries. */
const personColumns = {
  personId: uuidSchema,
  personName: z.string(),
  employeeCode: z.string(),
  legalEntityId: uuidSchema,
  legalEntityName: z.string(),
  departmentName: z.string().nullable(),
  locationName: z.string().nullable(),
};

// ---------------------------------------------------------------------------
// Leave register
// ---------------------------------------------------------------------------

/**
 * One person's movement in one leave type and leave year over a period. A period that crosses a year
 * boundary yields a row per leave year (each year's balance is its own ledger), never one summed
 * figure. Every row reconciles:
 * `closing = opening + accrued + carriedForward + adjusted - used - encashed - lapsed`.
 * `used` is net of days returned by a cancel or a rejection and includes days held by requests still
 * awaiting approval (the ledger debits at filing). `encashed` is net of reversals. `lapsed` covers
 * `lapse` and `expiry` entries; days the scheduled year-end left behind without an entry are in the
 * lapse report, not here.
 */
export const leaveRegisterReportRowSchema = z.object({
  ...personColumns,
  leaveTypeId: uuidSchema,
  leaveTypeCode: z.string(),
  leaveTypeName: z.string(),
  leaveYear: z.number().int(),
  openingDays: dayFigureSchema,
  accruedDays: dayFigureSchema,
  carriedForwardDays: dayFigureSchema,
  adjustedDays: dayFigureSchema,
  usedDays: dayFigureSchema,
  /** Paid days taken off the balance by requests still awaiting approval: not in `usedDays`.
   *  Optional so an older API's rows still parse. */
  pendingDays: dayFigureSchema.optional(),
  encashedDays: dayFigureSchema,
  lapsedDays: dayFigureSchema,
  closingDays: dayFigureSchema,
});
export type LeaveRegisterReportRow = z.infer<typeof leaveRegisterReportRowSchema>;

export const leaveRegisterReportQuerySchema = paginationQuerySchema.extend(reportFilterShape);

export const leaveRegisterExportQuerySchema = z.object(reportFilterShape);

export const leaveReportCsvExportSchema = z.object({
  filename: z.string(),
  contentType: z.literal('text/csv'),
  /** The whole file, header row first, CRLF line endings. */
  csv: z.string(),
  rowCount: z.number().int().nonnegative(),
  /** True when the file was cut at `LEAVE_REPORT_EXPORT_MAX_ROWS`; narrow the filters. */
  truncated: z.boolean(),
});
export type LeaveReportCsvExport = z.infer<typeof leaveReportCsvExportSchema>;

// ---------------------------------------------------------------------------
// Lapse report
// ---------------------------------------------------------------------------

/**
 * Why days left a balance without being taken or paid.
 *   - `lapse`            — a `lapse` ledger entry (HR's year-end run wrote the days off).
 *   - `expiry`           — an `expiry` ledger entry (days that carried forward and ran out).
 *   - `rollover_forfeit` — days a year closed with that the next year's `carry_forward` entry did
 *                          not carry (the scheduled year-end writes no entry for the remainder, so
 *                          the report derives it from the two ledger rows).
 */
export const leaveLapseKindSchema = z.enum(['lapse', 'expiry', 'rollover_forfeit']);
export type LeaveLapseKind = z.infer<typeof leaveLapseKindSchema>;

export const leaveLapseReportRowSchema = z.object({
  ...personColumns,
  leaveTypeId: uuidSchema,
  leaveTypeCode: z.string(),
  leaveTypeName: z.string(),
  /** The leave year the days belonged to. */
  leaveYear: z.number().int(),
  kind: leaveLapseKindSchema,
  /** Always positive. */
  days: dayFigureSchema,
  /** The day the entry was written (for `rollover_forfeit`, the day the next year opened). */
  recordedOn: dateOnlySchema,
});
export type LeaveLapseReportRow = z.infer<typeof leaveLapseReportRowSchema>;

export const leaveLapseReportQuerySchema = paginationQuerySchema.extend({
  ...reportFilterShape,
  kind: leaveLapseKindSchema.optional(),
});

export const leaveLapseReportSchema = paginatedSchema(leaveLapseReportRowSchema).extend({
  /** Over every matching row, not just this page. */
  totals: z.object({
    lapsedDays: dayFigureSchema,
    expiredDays: dayFigureSchema,
    rolloverForfeitedDays: dayFigureSchema,
  }),
});
export type LeaveLapseReport = z.infer<typeof leaveLapseReportSchema>;

// ---------------------------------------------------------------------------
// Trends
// ---------------------------------------------------------------------------

/** `approved` (default) is leave taken or booked; `pending` is leave still awaiting a decision. */
export const leaveTrendsStatusSchema = z.enum(['approved', 'pending']);

export const leaveTrendsQuerySchema = z.object({
  ...reportFilterShape,
  status: leaveTrendsStatusSchema.default('approved'),
});

/**
 * One cell: requests that START in `month` (a request is counted once, in the month it begins, so a
 * request spanning a month end is not split), for one department and leave type. The department is
 * the person's department on the request's start date (effective-dated, invariant 5).
 */
export const leaveTrendRowSchema = z.object({
  month: z.string().regex(/^\d{4}-\d{2}$/, 'Must be YYYY-MM'),
  departmentId: uuidSchema.nullable(),
  departmentName: z.string().nullable(),
  leaveTypeId: uuidSchema,
  leaveTypeCode: z.string(),
  leaveTypeName: z.string(),
  requestCount: z.number().int().nonnegative(),
  days: dayFigureSchema,
  paidDays: dayFigureSchema,
  unpaidDays: dayFigureSchema,
});
export type LeaveTrendRow = z.infer<typeof leaveTrendRowSchema>;

export const leaveTrendsReportSchema = z.object({
  items: z.array(leaveTrendRowSchema),
  totals: z.object({ requestCount: z.number().int().nonnegative(), days: dayFigureSchema }),
  /** True when the cells were cut at `LEAVE_TRENDS_MAX_CELLS`; narrow the filters. */
  truncated: z.boolean(),
});
export type LeaveTrendsReport = z.infer<typeof leaveTrendsReportSchema>;

// ---------------------------------------------------------------------------
// Loss of pay
// ---------------------------------------------------------------------------

/**
 * One person's unpaid leave in a period. `lopDays` is the payroll rule's figure — the same one the
 * payroll run adds to attendance loss of pay (`unpaidLeaveLopDays`): unpaid days charged only
 * against leave-covered days inside the days the person was employed. `unpaidLeaveDays` is the
 * plain sum of the unpaid portion of the approved requests that overlap the period.
 */
export const leaveLopReportRowSchema = z.object({
  ...personColumns,
  requestCount: z.number().int().nonnegative(),
  unpaidLeaveDays: dayFigureSchema,
  lopDays: dayFigureSchema,
});
export type LeaveLopReportRow = z.infer<typeof leaveLopReportRowSchema>;

export const leaveLopReportQuerySchema = paginationQuerySchema.extend({
  from: dateOnlySchema,
  to: dateOnlySchema,
  legalEntityId: uuidSchema.optional(),
  departmentId: uuidSchema.optional(),
  locationId: uuidSchema.optional(),
  search: z.string().trim().max(100).optional(),
});

// ---------------------------------------------------------------------------
// Holiday bulk import
// ---------------------------------------------------------------------------

/**
 * The CSV's header: `name,date,locations,optional,published`. Column order is free and names are
 * matched case-insensitively; `restricted` is accepted for `optional`. `locations` is one or more
 * location codes or names separated by `;` or `|`, or `all` for every active location. `optional`
 * marks a restricted/floating holiday (default no). `published` defaults to NO: an imported calendar
 * is staged as a draft for HR to review, exactly like the Holiday console's own staging.
 */
export const HOLIDAY_IMPORT_CSV_COLUMNS = ['name', 'date', 'locations'] as const;
export const HOLIDAY_IMPORT_MAX_ROWS = 500;
export const HOLIDAY_IMPORT_MAX_CSV_CHARS = 200_000;
export const HOLIDAY_IMPORT_ALL_LOCATIONS = 'all';

/** A row as the client parsed it. Deliberately loose: a bad value is a per-row error in the
 * preview, not a rejected request. */
export const holidayImportRowInputSchema = z.object({
  name: z.string().max(300),
  date: z.string().max(40),
  locations: z.array(z.string().max(120)).max(200),
  isOptional: z.boolean().optional(),
  isPublished: z.boolean().optional(),
});
export type HolidayImportRowInput = z.infer<typeof holidayImportRowInputSchema>;

/** Exactly one of `csv` or `rows`; the API reports anything else as a file error. */
export const holidayImportBodySchema = z
  .object({
    csv: z.string().min(1).max(HOLIDAY_IMPORT_MAX_CSV_CHARS).optional(),
    rows: z.array(holidayImportRowInputSchema).min(1).max(HOLIDAY_IMPORT_MAX_ROWS).optional(),
  })
  .strict();
export type HolidayImportBody = z.infer<typeof holidayImportBodySchema>;

/**
 *   - `create`            — will be created.
 *   - `exists`            — the location already has a holiday on that date; skipped.
 *   - `duplicate_in_file` — an earlier row of this file already covers that location and date; skipped.
 *   - `error`             — could not be resolved (unknown location, no permission); the row has errors.
 */
export const holidayImportTargetStatusSchema = z.enum([
  'create',
  'exists',
  'duplicate_in_file',
  'error',
]);
export type HolidayImportTargetStatus = z.infer<typeof holidayImportTargetStatusSchema>;

export const holidayImportTargetSchema = z.object({
  /** What the row said, as typed. */
  input: z.string(),
  locationId: uuidSchema.nullable(),
  locationCode: z.string().nullable(),
  locationName: z.string().nullable(),
  status: holidayImportTargetStatusSchema,
});
export type HolidayImportTarget = z.infer<typeof holidayImportTargetSchema>;

export const holidayImportPreviewRowSchema = z.object({
  /** 1-based; for a CSV the header is row 1, so the first data row is 2. */
  rowNumber: z.number().int().positive(),
  name: z.string(),
  /** Null when the date could not be read. */
  date: dateOnlySchema.nullable(),
  isOptional: z.boolean(),
  isPublished: z.boolean(),
  targets: z.array(holidayImportTargetSchema),
  errors: z.array(z.string()),
});
export type HolidayImportPreviewRow = z.infer<typeof holidayImportPreviewRowSchema>;

export const holidayImportPreviewSchema = z.object({
  /** Problems with the file as a whole. Rows are empty when any is present. */
  fileErrors: z.array(z.string()),
  rows: z.array(holidayImportPreviewRowSchema),
  createCount: z.number().int().nonnegative(),
  existsCount: z.number().int().nonnegative(),
  duplicateInFileCount: z.number().int().nonnegative(),
  /** Rows with at least one error. */
  errorCount: z.number().int().nonnegative(),
});
export type HolidayImportPreview = z.infer<typeof holidayImportPreviewSchema>;

export const holidayImportResultSchema = z.object({
  createdCount: z.number().int().nonnegative(),
  /** Location-and-date pairs that already had a holiday (in the file twice, or created meanwhile). */
  skippedCount: z.number().int().nonnegative(),
});
export type HolidayImportResult = z.infer<typeof holidayImportResultSchema>;
